import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import Login from './components/Login';
import SignUp from './components/SignUp';
import Home from './components/Home';
import Navbar from './components/Navbar';
import ProfileSetup from './components/ProfileSetup';
import Stars from './components/Stars';
import Messages from './components/Messages';
import Profile from './components/Profile';
import Upload from './components/Upload';
import Search from './components/Search';
import TopBar from './components/TopBar';
import Discography from './components/Discography';
import Settings from './components/Settings';
import AudioPlayer from './components/AudioPlayer';
import OtherUserDiscography from './components/OtherUserDiscography';
import Success from './pages/Success';
import Cancel from './pages/Cancel';
import ProtectedRoute from './components/ProtectedRoute';
import AuthRoute from './components/AuthRoute';
import { AudioProvider, AudioContext } from './context/AudioContext';
import './App.css';
import TopTracks from './components/TopTracks';
import AllTracks from './components/AllTracks';
import LikedTracks from './components/LikedTracks';
// Placeholder components (commented out as in original)
//import Earnings from './components/Earnings';
//import SubscriptionScreen from './components/SubscriptionScreen';
//import SubscriptionSuccess from './components/SubscriptionSuccess';
import TrackDetail from './components/TrackDetail';
import AlbumDetail from './components/AlbumDetail';
import OtherUserTopTracks from './components/OtherUserTopTracks';
import OtherUserAllTracks from './components/OtherUserAllTracks';
import OtherUserAlbumDetail from './components/OtherUserAlbumDetail';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import EndUserLicenseAgreement from './components/EndUserLicenseAgreement';
import Earnings from './components/Earnings';

function App() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [profileComplete, setProfileComplete] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      const fetchUserProfile = async () => {
        try {
          const response = await axios.get(`https://sync-api-troz.onrender.com/api/users/firebase/${user.uid}`);
          setProfileComplete(!!response.data.artistName);
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setProfileComplete(false);
        }
      };
      fetchUserProfile();
    } else {
      setProfileComplete(null);
    }
  }, [user]);

  if (loading || (user && profileComplete === null)) {
    return (
      <div className="loading-container">
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <AudioProvider>
      <Router>
        <div className="app-container">
          <Routes>
            {/* Unprotected Routes */}
            <Route element={<LayoutWithTopBar />}>
              <Route path="/profile-setup" element={<ProfileSetup setProfileComplete={setProfileComplete} />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/end-user-license-agreement" element={<EndUserLicenseAgreement />} />
            </Route>

            {/* Auth Routes */}
            <Route element={<AuthRoute />}>
              <Route element={<LayoutWithTopBar />}>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
              </Route>
            </Route>

            {/* Protected Routes */}
            <Route element={<ProtectedRoute />}>
              <Route element={<LayoutWithNavbar />}>
                <Route path="/" element={<Home />} />
                <Route path="/home" element={<Home />} />
                <Route path="/stars" element={<Stars />} />
                <Route path="/upload" element={<Upload />} />
                <Route path="/search" element={<Search />} />
                <Route path="/my-discography" element={<DiscographyNavigator />} />
                <Route path="/my-discography/top-tracks" element={<TopTracks />} />
                <Route path="/my-discography/all-tracks" element={<AllTracks />} />
                <Route path="/my-discography/liked-tracks" element={<LikedTracks />} />
                <Route path="/otheruserdiscography/:id/top-tracks" element={<OtherUserTopTracks />} />
                <Route path="/otheruserdiscography/:id/all-tracks" element={<OtherUserAllTracks />} />
                <Route path="/otheruserdiscography/:id/album/:albumId" element={<OtherUserAlbumDetail />} />
                <Route path="/discography/album/:albumId" element={<AlbumDetail />} />
                {/* <Route path="/earnings" element={<Earnings />} /> */}
                <Route path="/profile" element={<Profile />} />
                <Route path="/messages" element={<Messages />} />
                <Route path="/earnings" element={<Earnings />} />
                {/* <Route path="/settings" element={<Settings />} /> */}
                {/* <Route path="/subscription" element={<SubscriptionScreen />} /> */}
                {/* <Route path="/subscription-success" element={<SubscriptionSuccess />} /> */}
                <Route path="/success" element={<Success />} />
                <Route path="/cancel" element={<Cancel />} />
                <Route path="/trackdetail/:trackId" element={<TrackDetail />} />
                <Route path="/otheruserdiscography/:id" element={<OtherUserDiscographyNavigator />} />
              </Route>
            </Route>
          </Routes>
        </div>
      </Router>
    </AudioProvider>
  );
}

function LayoutWithTopBar() {
  return (
    <>
      <TopBar />
      <Outlet />
    </>
  );
}

function LayoutWithNavbar() {
  return (
    <>
      <TopBar />
      <Outlet />
      <Navbar />
      <AudioPlayerContainer />
    </>
  );
}

function DiscographyNavigator() {
  return (
    <Routes>
      <Route path="/" element={<Discography />} />
    </Routes>
  );
}

function OtherUserDiscographyNavigator() {
  const location = useLocation();
  const basePath = location.pathname.split('/top-tracks')[0].split('/all-tracks')[0].split('/album')[0];

  return (
    <Routes>
      <Route path="/" element={<OtherUserDiscography />} />
      <Route path="top-tracks" element={<OtherUserTopTracks />} />
      <Route path="all-tracks" element={<OtherUserAllTracks />} />
      <Route path="album/:albumId" element={<OtherUserAlbumDetail />} />
    </Routes>
  );
}

function AudioPlayerContainer() {
  const { currentTrack, autoPlay } = React.useContext(AudioContext);
  return currentTrack ? <AudioPlayer track={currentTrack} autoPlay={autoPlay} /> : null;
}

export default App;